import type { ProductId, ProductKind } from "@highmarch/app";
import { parseOffers } from "@highmarch/domain";
import shop from "../../../../../balance/src/shop.json";
import offers from "../../../../../balance/src/offers.json";
import seasons from "../../../../../balance/src/seasons.json";

/**
 * The real-money products (FINISH_PLAN D9, ADR 0013), derived from the one list that defines them: balance/src/shop.json
 * `gemPacks` (id, name, gems, reference price), which also maps each product to its grant (@highmarch/app PurchaseService).
 * Everything else that names them reads this: the StoreKit configuration (ios/App/App/Highmarch.storekit,
 * regenerated and checked by tools/web/check-ios.ts), the sandbox store's catalogue, and the App Store Connect records
 * Kevin creates (docs/BUILD.md, iOS). An id is the App Store product id as it is: App Store Connect accepts letters,
 * digits, `_` and `.`, and an id can never be reused once created, so they are final once Kevin creates them.
 *
 * Every product is consumable. Since 2026-09-26 (docs/SHOP.md) the list is the gem packs, then every Deals product
 * (balance/src/offers.json: each offer, the Daily Deals tiers and Buy All), in catalog order, then the season pass
 * (balance/src/seasons.json: Premium, Premium+; MP-22); @highmarch/app
 * `allStoreProducts` builds the same list from the balance bundle (a test keeps them equal).
 */
export interface ProductListing {
  readonly id: ProductId;
  readonly kind: ProductKind;
  readonly displayName: string;
  /** The App Store price tier in USD the .storekit file uses ("1.99"); the real store localises its own. */
  readonly referencePriceUsd: string;
}

/** App Store product ids: letters, digits, `_` and `.` (App Store Connect's rule). */
const APP_STORE_ID = /^[A-Za-z0-9_.]+$/;

/** shop.json's gem packs as store listings (the reference price loses its "$": StoreKit files hold the bare amount). */
export function listingsFrom(packs: ReadonlyArray<{ readonly id: string; readonly name: string; readonly referencePrice: string }>): ProductListing[] {
  return packs.map((p) => {
    if (!APP_STORE_ID.test(p.id)) throw new Error(`shop.json gemPacks: "${p.id}" is not a valid App Store product id`);
    return { id: p.id, kind: "consumable", displayName: p.name, referencePriceUsd: p.referencePrice.replace(/^\$/, "") };
  });
}

/** offers.json's products (each offer, then the Daily Deals tiers and Buy All), page by page. */
export function dealListings(raw: unknown): { id: string; name: string; referencePrice: string }[] {
  return parseOffers({ offers: raw }).pages.flatMap((p) => [
    ...p.offers.map((o) => ({ id: o.price.product, name: o.name, referencePrice: o.price.label })),
    ...(p.daily ? [...p.daily.tiers.map((t, i) => ({ id: t.price.product, name: `Daily Deal ${i + 1}`, referencePrice: t.price.label })), { id: p.daily.buyAll.product, name: "Daily Deals: Buy All", referencePrice: p.daily.buyAll.label }] : []),
  ]);
}

/** seasons.json's pass products: Premium, then Premium+ (MP-22). Consumable: a pass is bought again each season. */
export function passListings(raw: { readonly premium: PassJson; readonly premiumPlus: PassJson }): { id: string; name: string; referencePrice: string }[] {
  return [raw.premium, raw.premiumPlus].map((p) => ({ id: p.product, name: p.name, referencePrice: p.price }));
}
interface PassJson { readonly product: string; readonly name: string; readonly price: string }

export const GEM_PACK_LISTINGS: readonly ProductListing[] = listingsFrom(shop.gemPacks);
export const PRODUCT_LISTINGS: readonly ProductListing[] = [...GEM_PACK_LISTINGS, ...listingsFrom(dealListings(offers)), ...listingsFrom(passListings(seasons))];
export const STORE_PRODUCT_IDS: readonly ProductId[] = PRODUCT_LISTINGS.map((p) => p.id);

export function isStoreProductId(id: string): boolean {
  return STORE_PRODUCT_IDS.includes(id);
}
